// ═══════════════════════════════════════════════════════════
// TanStack Query Key Factory (TPP §2.3)
// Hierarchical keys for precise cache invalidation
// ═══════════════════════════════════════════════════════════

export const queryKeys = {
  rules: {
    all: ["rules"] as const,
    list: (params?: unknown) => ["rules", "list", params] as const,
    detail: (ruleId: number) => ["rules", "detail", ruleId] as const,
  },

  commissions: {
    all: ["commissions"] as const,
    list: (params?: Record<string, unknown>) => ["commissions", "list", params] as const,
    detail: (entryId: number) => ["commissions", "detail", entryId] as const,
    /** Accepts a legacy period string OR a { dateFrom, dateTo, dateBasis } range object. */
    summary: (params: unknown) => ["commissions", "summary", params] as const,
  },

  settings: ["settings"] as const,

  tokenHealth: ["token-health"] as const,

  storageHealth: ["storage-health"] as const,

  dataRegions: ["data-regions"] as const,

  webhookStatus: ["webhook-status"] as const,

  historicalSync: ["historical-sync"] as const,

  license: {
    status: ["license", "status"] as const,
    plans: ["license", "plans"] as const,
    usage: ["license", "usage"] as const,
  },

  b24: {
    root: ["b24"] as const,
    categories: (entityTypeId: number) => ["b24", "categories", entityTypeId] as const,
    stages: (entityTypeId: number) => ["b24", "stages", entityTypeId] as const,
    stagesGrouped: (entityTypeId: number) => ["b24", "stagesGrouped", entityTypeId] as const,
    currencies: () => ["b24", "currencies"] as const,
    users: (search: string) => ["b24", "users", search] as const,
    usersByIds: (ids: number[]) => ["b24", "usersByIds", ids] as const,
    entityTypes: () => ["b24", "entityTypes"] as const,
    fields: (entityTypeId: number) => ["b24", "fields", entityTypeId] as const,
    products: (search: string) => ["b24", "products", search] as const,
    productsValidate: (ids: number[]) => ["b24", "products", "validate", ids] as const,
    productPrices: (ids: number[], mode: "direct" | "proxy") =>
      ["b24", "productPrices", mode, ids] as const,
    catalogInfo: (mode: "direct" | "proxy") => ["b24", "catalogInfo", mode] as const,
    catalogProductFields: () => ["b24", "catalogProductFields"] as const,
  },

  metrics: {
    all: ["metrics"] as const,
    totals: (repId: number, period: string, ruleId?: number) =>
      ["metrics", "totals", repId, period, ruleId ?? 0] as const,
    totalsRange: (params: unknown) => ["metrics", "totalsRange", params] as const,
    recompute: ["metrics", "recompute"] as const,
  },

  dashboard: {
    all: ["dashboard"] as const,
    topPerformers: (params: unknown) => ["dashboard", "topPerformers", params] as const,
    revenueLeaders: (params: unknown) => ["dashboard", "revenueLeaders", params] as const,
    volumeLeaders: (params: unknown) => ["dashboard", "volumeLeaders", params] as const,
    statusBreakdown: (params: unknown) => ["dashboard", "statusBreakdown", params] as const,
    efficiency: (params: unknown) => ["dashboard", "efficiency", params] as const,
  },

  snapshots: {
    all: ["snapshots"] as const,
    byPeriod: (period: string) => ["snapshots", "period", period] as const,
    byDate: (date: string) => ["snapshots", "date", date] as const,
  },

  eventLog: {
    all: ["event-log"] as const,
    list: (params?: unknown) => ["event-log", "list", params] as const,
    detail: (eventId: string) => ["event-log", "detail", eventId] as const,
    facets: () => ["event-log", "facets"] as const,
    summary: (params?: unknown) => ["event-log", "summary", params] as const,
  },

  activityLog: {
    all: ["activity-log"] as const,
    feed: (params?: unknown) => ["activity-log", "feed", params] as const,
    timeline: (entryId: number) => ["activity-log", "timeline", entryId] as const,
  },

  integrations: {
    apiKey: () => ["integrations", "api-key"] as const,
    webhook: () => ["integrations", "webhook"] as const,
  },
} as const;
